import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { IconLoader, IconReceipt, IconAlert } from "../components/Icons";

interface Purchase {
  id: string;
  device_id: string;
  user_email: string | null;
  period: string;
  status: string;
  initiated_at: string;
  completed_at: string | null;
  price_minor: number;
  currency: string;
  invoice_number: string | null;
  invoice_id: string | null;
}

const STATUS_KEYS = ["initiated", "completed", "cancelled", "failed"];
const PERIOD_KEYS = ["month", "year", "trial"];

export default function LicenseHistory() {
  const { t } = useTranslation();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/licenses/purchases");
      setPurchases(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t("licenseHistory.loadError"));
    } finally {
      setLoading(false);
    }
  }

  async function downloadPdf(invoiceId: string, invoiceNumber: string | null) {
    setDownloading(invoiceId);
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(t("licenseHistory.downloadError"));
    } finally {
      setDownloading(null);
    }
  }

  const fmt = (d: string | null) => d ? new Date(d).toLocaleString("cs-CZ") : "—";

  return (
    <div>
      <div className="page-header">
        <h1>{t("licenseHistory.title")}</h1>
        <p>{t("licenseHistory.subtitle")}</p>
      </div>

      {error && (
        <div className="card" style={{ background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <IconAlert size={18} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <IconLoader size={32} className="muted" />
          <p style={{ color: "var(--muted)", marginTop: 12 }}>{t("licenseHistory.loading")}</p>
        </div>
      ) : purchases.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <IconReceipt size={48} className="muted" />
          <h3 style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>{t("licenseHistory.empty")}</h3>
          <p style={{ color: "var(--muted)", marginTop: 8, fontSize: 14 }}>
            {t("licenseHistory.emptyHint")}
          </p>
        </div>
      ) : (
        <div className="card" style={{ overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)", textAlign: "left" }}>
                <th style={{ padding: "12px 16px" }}>{t("licenseHistory.colInitiated")}</th>
                <th style={{ padding: "12px 16px" }}>{t("licenseHistory.colPeriod")}</th>
                <th style={{ padding: "12px 16px" }}>{t("orders.colPrice")}</th>
                <th style={{ padding: "12px 16px" }}>{t("common.status")}</th>
                <th style={{ padding: "12px 16px" }}>{t("common.user")}</th>
                <th style={{ padding: "12px 16px" }}>{t("licenseHistory.colInvoice")}</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                  <td style={{ padding: "12px 16px" }}>{fmt(p.initiated_at)}</td>
                  <td style={{ padding: "12px 16px" }}>{PERIOD_KEYS.includes(p.period) ? t(`licenseHistory.period.${p.period}`) : p.period}</td>
                  <td style={{ padding: "12px 16px" }}>{(p.price_minor / 100).toFixed(2)} {p.currency}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span className={`badge badge-${p.status === "completed" ? "green" : p.status === "cancelled" || p.status === "failed" ? "red" : "yellow"}`}>
                      {STATUS_KEYS.includes(p.status) ? t(`licenseHistory.status.${p.status}`) : p.status}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>{p.user_email || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {p.invoice_id ? (
                      <button
                        className="btn-ghost"
                        onClick={() => void downloadPdf(p.invoice_id!, p.invoice_number)}
                        disabled={downloading === p.invoice_id}
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                      >
                        {downloading === p.invoice_id ? <IconLoader size={14} /> : <IconReceipt size={14} />}
                        {p.invoice_number || "PDF"}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
